/**
 * The app's only button.
 *
 * Three variants cover every CTA in the designs: the filled green primary
 * ("Donate a Meal", "Confirm Order"), the outlined secondary ("See Impact",
 * "Back to Home"), and a text-only tertiary for inline actions like "Edit".
 */

import React, { memo } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  View,
  type AccessibilityRole,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { colors, radius, shadow, sizing, space } from '../../theme';
import { Text } from './Text';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'danger';
export type ButtonSize = 'md' | 'lg';

export interface ButtonProps {
  label: string;
  onPress?: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  loading?: boolean;
  /** Rendered before the label, e.g. the lock on "Confirm Order". */
  leadingIcon?: React.ReactNode;
  /** Rendered after the label, e.g. the arrow on "Proceed to Payment". */
  trailingIcon?: React.ReactNode;
  fullWidth?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
  accessibilityHint?: string;
}

export const Button = memo(function Button({
  label,
  onPress,
  variant = 'primary',
  size = 'lg',
  disabled = false,
  loading = false,
  leadingIcon,
  trailingIcon,
  fullWidth = true,
  style,
  testID,
  accessibilityHint,
}: ButtonProps) {
  const isDisabled = disabled || loading;

  return (
    <Pressable
      testID={testID}
      onPress={onPress}
      disabled={isDisabled}
      accessibilityRole={'button' as AccessibilityRole}
      accessibilityLabel={label}
      accessibilityHint={accessibilityHint}
      accessibilityState={{ disabled: isDisabled, busy: loading }}
      style={({ pressed }) => [
        styles.base,
        styles[size],
        variantStyles[variant].container,
        fullWidth && styles.fullWidth,
        pressed && !isDisabled && variantStyles[variant].pressed,
        isDisabled && variantStyles[variant].disabled,
        style,
      ]}
    >
      {loading ? (
        <ActivityIndicator
          color={variant === 'primary' || variant === 'danger' ? colors.text.onBrand : colors.text.brand}
        />
      ) : (
        <View style={styles.content}>
          {leadingIcon ? <View style={styles.icon}>{leadingIcon}</View> : null}
          <Text variant="button" tone={variantStyles[variant].tone} numberOfLines={1}>
            {label}
          </Text>
          {trailingIcon ? <View style={styles.icon}>{trailingIcon}</View> : null}
        </View>
      )}
    </Pressable>
  );
});

const styles = StyleSheet.create({
  base: {
    borderRadius: radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: sizing.touchTarget,
  },
  md: {
    paddingVertical: space.md,
    paddingHorizontal: space.lg,
  },
  lg: {
    paddingVertical: space.lg,
    paddingHorizontal: space.xl,
  },
  fullWidth: {
    alignSelf: 'stretch',
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.sm,
  },
  icon: {
    justifyContent: 'center',
  },
});

const variantStyles = {
  primary: {
    container: {
      backgroundColor: colors.action.primaryBg,
      // A border in its own fill colour, so a primary and a secondary button
      // are exactly the same height. Only the secondary carried one, which
      // left every filled button 2px shorter than the outlined one beside it.
      borderWidth: 1,
      borderColor: colors.action.primaryBg,
      ...shadow('card'),
    } as ViewStyle,
    pressed: { backgroundColor: colors.action.primaryPressed } as ViewStyle,
    disabled: { backgroundColor: colors.action.primaryDisabled } as ViewStyle,
    tone: 'onBrand' as const,
  },
  secondary: {
    container: {
      backgroundColor: colors.bg.surface,
      borderWidth: 1,
      borderColor: colors.action.secondaryBorder,
    } as ViewStyle,
    pressed: { backgroundColor: colors.action.secondaryPressed } as ViewStyle,
    disabled: { opacity: 0.5 } as ViewStyle,
    tone: 'brand' as const,
  },
  tertiary: {
    container: {
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: 'transparent',
    } as ViewStyle,
    pressed: { backgroundColor: colors.bg.pressed } as ViewStyle,
    disabled: { opacity: 0.5 } as ViewStyle,
    tone: 'brand' as const,
  },
  danger: {
    container: {
      backgroundColor: colors.action.dangerBg,
      borderWidth: 1,
      borderColor: colors.action.dangerBg,
    } as ViewStyle,
    pressed: { opacity: 0.85 } as ViewStyle,
    disabled: { opacity: 0.5 } as ViewStyle,
    tone: 'onBrand' as const,
  },
} as const;
